import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { productDetails } from '../../mocks/productDetails';
import { hotProducts } from '../../mocks/products';
import { usePageMeta } from '../../hooks/usePageMeta';

type Plan = {
  id: number;
  amount: string;
  price: string;
  originalPrice: string;
  discount: string;
  badge?: string;
};

type PlanGroup = {
  groupName: string;
  icon: string;
  plans: Plan[];
};

type ProductDetail = {
  id: string;
  name: string;
  subtitle?: string;
  category: string;
  badge?: string;
  description: string;
  warning?: string;
  servers?: string[];
  isInternational?: boolean;
  originalTWD?: number;
  discountRate?: number;
  purchaseItem?: string;
  planGroups?: PlanGroup[];
  plans: Plan[];
  reviews: { id: number; user: string; rating: number; comment: string; date: string; avatar: string }[];
  faqs: { question: string; answer: string }[];
};

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const product = id ? (productDetails[id as keyof typeof productDetails] as unknown as ProductDetail) : null;

  // 設定產品頁 Meta
  usePageMeta({
    title: product ? `${product.name}${product.subtitle || ''} 儲值 | laigame手遊代儲` : 'laigame手遊代儲',
    description: product 
      ? `${product.name}${product.subtitle || ''} 專業代儲服務，最優惠折扣，3秒快速到帳，24小時全年無休，安全有保障，專業客服團隊，立即選購享受超值優惠！`
      : 'laigame手遊代儲提供專業遊戲儲值服務',
    keywords: product 
      ? `${product.name},${product.name}儲值,${product.name}代儲,手遊代儲,遊戲儲值,UID儲值,laigame`
      : '手遊代儲,遊戲儲值',
    ogTitle: product ? `${product.name}${product.subtitle || ''} 儲值 | laigame手遊代儲` : 'laigame手遊代儲',
    ogDescription: product 
      ? `${product.name}${product.subtitle || ''} 專業代儲服務，最優惠折扣，3秒快速到帳，安全有保障`
      : 'laigame手遊代儲提供專業遊戲儲值服務',
    ogImage: product 
      ? `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28product.name%20%20%20%20game%20official%20artwork%29%7D&width=1200&height=630&seq=prod-og-${id}&orientation=landscape`
      : undefined
  });

  const hasGroups = !!(product as ProductDetail)?.planGroups?.length;
  const firstGroup = hasGroups ? (product as ProductDetail).planGroups![0] : null;

  const [activeGroup, setActiveGroup] = useState<string>(firstGroup?.groupName ?? '');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(
    firstGroup ? firstGroup.plans[1] ?? firstGroup.plans[0] : product?.plans[1] ?? product?.plans[0] ?? null
  );
  const [uid, setUid] = useState('');
  const [server, setServer] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 35, seconds: 48 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (product && selectedPlan) {
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://laigame.cc';
      const productImageUrl = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28product.name%20%20%20%20game%20official%20artwork%29%7D&width=800&height=600&seq=prod-${id}&orientation=landscape`;
      
      // 計算平均評分
      const totalRating = product.reviews.reduce((sum, review) => sum + review.rating, 0);
      const avgRating = (totalRating / product.reviews.length).toFixed(1);
      
      // 1. Product Schema - 完整產品結構化資料
      const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": `${product.name}${product.subtitle || ''} - ${selectedPlan.amount}`,
        "description": product.description,
        "image": productImageUrl,
        "sku": `${id}-${selectedPlan.id}`,
        "category": product.category,
        "brand": {
          "@type": "Brand",
          "name": "laigame手遊代儲"
        },
        "offers": {
          "@type": "Offer",
          "url": `${siteUrl}/product/${id}`,
          "priceCurrency": "TWD",
          "price": selectedPlan.price.replace(/[^0-9]/g, ''),
          "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          "availability": "https://schema.org/InStock",
          "itemCondition": "https://schema.org/NewCondition",
          "seller": {
            "@type": "Organization",
            "name": "laigame手遊代儲"
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": avgRating,
          "reviewCount": product.reviews.length,
          "bestRating": "5",
          "worstRating": "1"
        },
        "review": product.reviews.map(review => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.user
          },
          "datePublished": review.date,
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.rating,
            "bestRating": "5",
            "worstRating": "1"
          },
          "reviewBody": review.comment
        }))
      };

      // 2. BreadcrumbList Schema - 麵包屑導航
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首頁",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "UID自動儲值",
            "item": `${siteUrl}/games`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${product.name}${product.subtitle || ''}`,
            "item": `${siteUrl}/product/${id}`
          }
        ]
      };

      // 3. FAQPage Schema - 常見問題
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": product.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };

      // 注入所有 Schema
      const productScript = document.createElement('script');
      productScript.type = 'application/ld+json';
      productScript.text = JSON.stringify(productSchema);
      productScript.id = 'product-schema';
      document.head.appendChild(productScript);

      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
      breadcrumbScript.id = 'breadcrumb-schema';
      document.head.appendChild(breadcrumbScript);

      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.text = JSON.stringify(faqSchema);
      faqScript.id = 'faq-schema';
      document.head.appendChild(faqScript);

      // 清除所有 Schema
      return () => {
        const schemas = ['product-schema', 'breadcrumb-schema', 'faq-schema'];
        schemas.forEach(schemaId => {
          const existingScript = document.getElementById(schemaId);
          if (existingScript) {
            document.head.removeChild(existingScript);
          }
        });
      };
    }
  }, [product, selectedPlan, id]);

  const currentPlans = hasGroups
    ? (product as ProductDetail).planGroups!.find(g => g.groupName === activeGroup)?.plans ?? []
    : product?.plans ?? [];

  const handleGroupChange = (groupName: string) => {
    setActiveGroup(groupName);
    const group = (product as ProductDetail).planGroups!.find(g => g.groupName === groupName);
    if (group) setSelectedPlan(group.plans[1] ?? group.plans[0]);
  };

  const handleAddToCart = () => {
    if (!uid || !server) return;
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-800">產品不存在</h1>
          <Link to="/" className="mt-4 inline-block text-indigo-600 hover:underline">返回首頁</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const priceNum = selectedPlan ? parseInt(selectedPlan.price.replace(/[^0-9]/g, '')) * quantity : 0;

  return (
    <div className="min-h-screen bg-[#f7f8fc]">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-indigo-600 transition-colors cursor-pointer">首頁</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="hover:text-indigo-600 transition-colors cursor-pointer">UID自動儲值</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800 font-medium">{product.name}{product.subtitle}</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)' }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #e94560 0%, transparent 50%), radial-gradient(circle at 80% 20%, #533483 0%, transparent 50%)' }}></div>
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-white">
              <div className="flex items-center gap-2 mb-4">
                {product.badge && (
                  <span className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    {product.badge}
                  </span>
                )}
                {product.isInternational && (
                  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    <i className="ri-global-line"></i>
                    國際服
                  </span>
                )}
              </div>
              <h1 className="text-4xl md:text-5xl font-black mb-2 leading-tight">
                {product.name}
              </h1>
              {product.subtitle && (
                <p className="text-xl text-white/70 mb-4">{product.subtitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/80 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <i className="ri-star-fill text-yellow-400"></i>
                  <span className="ml-1">4.9 分</span>
                </span>
                <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                <span><i className="ri-shopping-cart-2-line mr-1"></i>已售 28,500+</span>
                <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                <span><i className="ri-flashlight-line mr-1 text-yellow-400"></i>付款3秒入帳</span>
                <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                <span><i className="ri-time-line mr-1 text-green-400"></i>24小時全年無休</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/20">
                  <i className="ri-shield-check-line text-green-400 mr-2"></i>官方授權管道
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/20">
                  <i className="ri-user-line text-blue-400 mr-2"></i>角色無需下線
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/20">
                  <i className="ri-lock-line text-purple-400 mr-2"></i>安全有保障
                </div>
              </div>
            </div>
            {/* Countdown */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-white text-center min-w-[200px]">
              <div className="text-sm text-white/70 mb-2 flex items-center justify-center gap-1">
                <i className="ri-time-line text-red-400"></i>限時優惠倒計時
              </div>
              <div className="flex items-center justify-center gap-2 text-3xl font-black">
                <div className="bg-white/20 rounded-lg px-3 py-2">{String(timeLeft.hours).padStart(2, '0')}</div>
                <span className="text-white/60">:</span>
                <div className="bg-white/20 rounded-lg px-3 py-2">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <span className="text-white/60">:</span>
                <div className="bg-white/20 rounded-lg px-3 py-2">{String(timeLeft.seconds).padStart(2, '0')}</div>
              </div>
              <div className="text-xs text-white/50 mt-2">時 / 分 / 秒</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left */}
          <div className="lg:col-span-2 space-y-6">

            {/* Warning */}
            {product.warning && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-fill text-red-500 text-2xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-red-700 mb-1">重要注意事項</p>
                    <p className="text-red-600 text-sm leading-relaxed">{product.warning}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Groups */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-indigo-600"></i>
                選擇儲值方案
              </h2>

              {/* Group Tabs */}
              {hasGroups && (
                <div className="flex gap-2 mb-6 flex-wrap">
                  {(product as ProductDetail).planGroups!.map(group => (
                    <button
                      key={group.groupName}
                      onClick={() => handleGroupChange(group.groupName)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                        activeGroup === group.groupName
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <i className={group.icon}></i>
                      {group.groupName}
                    </button>
                  ))}
                </div>
              )}

              {/* Plans Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentPlans.map(plan => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan)}
                    className={`relative cursor-pointer rounded-xl p-4 border-2 transition-all duration-200 ${
                      selectedPlan?.id === plan.id
                        ? 'border-indigo-600 bg-indigo-50 shadow-md'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    {plan.badge && (
                      <span className="absolute -top-2.5 right-3 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-2.5 py-0.5 rounded-full font-bold shadow">
                        {plan.badge}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedPlan?.id === plan.id ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                      }`}>
                        {selectedPlan?.id === plan.id && <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>}
                      </div>
                      <span className="font-bold text-gray-800 text-sm">{plan.amount}</span>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-xl font-black text-indigo-600">{plan.price}</div>
                        <div className="text-xs text-gray-400 line-through">{plan.originalPrice}</div>
                      </div>
                      <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded font-bold">{plan.discount}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-indigo-600"></i>
                儲值說明
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm mb-4">{product.description}</p>
              
              {/* 購買物品說明 */}
              {product.purchaseItem && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shopping-bag-3-line text-indigo-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-indigo-900 mb-1">可購買物品</p>
                      <p className="text-indigo-700 text-sm leading-relaxed">{product.purchaseItem}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm text-gray-600">
                <p className="font-bold text-gray-700 mb-2">儲值須知：</p>
                <div className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-green-500 mt-0.5"></i><span>請提供訂單所需資料（UID + 伺服器）</span></div>
                <div className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-green-500 mt-0.5"></i><span>選擇付款方式並依所選方式繳費</span></div>
                <div className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-green-500 mt-0.5"></i><span>如有問題請聯繫客服LINE或FB粉絲團</span></div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: 'ri-user-search-line', label: '填寫UID', color: 'text-indigo-500' },
                  { icon: 'ri-server-line', label: '選擇伺服器', color: 'text-purple-500' },
                  { icon: 'ri-bank-card-line', label: '完成付款', color: 'text-green-500' },
                  { icon: 'ri-flashlight-line', label: '3秒入帳', color: 'text-orange-500' },
                ].map((step, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className={`text-2xl mb-1 ${step.color} w-8 h-8 flex items-center justify-center mx-auto`}>
                      <i className={step.icon}></i>
                    </div>
                    <p className="text-xs font-bold text-gray-700">{step.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-chat-smile-3-line text-indigo-600"></i>
                用戶評價
                <span className="ml-auto text-sm font-normal text-gray-400">{product.reviews.length} 則評價</span>
              </h2>
              <div className="flex items-center gap-6 mb-6 p-4 bg-indigo-50 rounded-xl">
                <div className="text-center">
                  <div className="text-5xl font-black text-indigo-600">4.9</div>
                  <div className="flex items-center justify-center gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">綜合評分</div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {[5,4,3,2,1].map(star => (
                    <div key={star} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 w-3">{star}</span>
                      <i className="ri-star-fill text-yellow-400"></i>
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div className="bg-yellow-400 h-1.5 rounded-full" style={{ width: star === 5 ? '90%' : star === 4 ? '8%' : '2%' }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                {product.reviews.map(review => (
                  <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-lg flex-shrink-0">
                        {review.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-gray-800 text-sm">{review.user}</span>
                          <span className="text-xs text-gray-400">{review.date}</span>
                        </div>
                        <div className="flex items-center gap-0.5 mb-2">
                          {[...Array(review.rating)].map((_, i) => <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>)}
                        </div>
                        <p className="text-gray-600 text-sm">{review.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-question-answer-line text-indigo-600"></i>
                常見問題
              </h2>
              <div className="space-y-2">
                {product.faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left"
                    >
                      <span className="font-bold text-gray-800 text-sm">{faq.question}</span>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform flex-shrink-0 ml-2 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 py-4 bg-white text-gray-600 text-sm leading-relaxed border-t border-gray-100">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-customer-service-2-line text-indigo-600"></i>
                聯絡客服
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-700">代儲服務時間</p>
                  <p><i className="ri-time-line text-green-500 mr-2"></i>24小時全年無休</p>
                  <p><i className="ri-phone-line text-indigo-500 mr-2"></i>0984-189784 / 07-9623029</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-700">社群聯絡</p>
                  <p><i className="ri-line-fill text-green-500 mr-2"></i>LINE ID：@laigame91</p>
                  <p><i className="ri-facebook-fill text-indigo-500 mr-2"></i>FB：laigame手遊代儲</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Purchase Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-4 text-lg">立即儲值</h3>

                {/* Selected Plan Summary */}
                {selectedPlan && (
                  <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4 mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-500">已選方案</p>
                      {product.isInternational && (
                        <span className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          <i className="ri-global-line"></i>
                          國際服
                        </span>
                      )}
                    </div>
                    <p className="font-bold text-gray-800 text-sm mb-2">{selectedPlan.amount}</p>
                    
                    {/* 價格折扣說明 */}
                    {product.originalTWD && product.discountRate && (
                      <div className="bg-white rounded-lg p-3 mb-3 border border-indigo-100">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">原價</span>
                          <span className="text-gray-400 line-through">NT$ {product.originalTWD}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">折扣</span>
                          <span className="text-orange-600 font-bold">{product.discountRate}折優惠</span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-indigo-100">
                          <span className="text-gray-700 font-bold text-sm">售價</span>
                          <span className="text-indigo-600 font-black text-lg">NT$ {Math.round(product.originalTWD * product.discountRate / 100)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-black text-indigo-600">{selectedPlan.price}</span>
                      <div className="text-right">
                        <span className="text-xs text-gray-400 line-through block">{selectedPlan.originalPrice}</span>
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded font-bold">{selectedPlan.discount}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* UID Input */}
                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">
                    遊戲 UID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={uid}
                    onChange={e => setUid(e.target.value)}
                    placeholder="請輸入您的遊戲UID（9位數字）"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors text-sm"
                  />
                </div>

                {/* Server Select */}
                {product.servers && (
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-1.5">
                      伺服器 <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={server}
                      onChange={e => setServer(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                    >
                      <option value="">-- 請選擇伺服器 --</option>
                      {product.servers.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantity */}
                <div className="mb-5">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">數量</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 hover:border-indigo-400 transition-colors cursor-pointer font-bold text-gray-600"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-lg font-bold text-gray-800 w-8 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 hover:border-indigo-400 transition-colors cursor-pointer font-bold text-gray-600"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                    {quantity > 1 && selectedPlan && (
                      <span className="text-sm text-gray-500 ml-1">合計 <strong className="text-indigo-600">NT$ {priceNum.toLocaleString()}</strong></span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-4 flex items-start gap-1">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  請確認UID及伺服器正確，充值成功後無法退款
                </p>

                {/* Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={!uid || !server}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3.5 rounded-xl hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                  >
                    {addedToCart ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-check-line"></i>已加入購物車！
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-shopping-cart-2-line"></i>加入購物車
                      </span>
                    )}
                  </button>
                  <button
                    disabled={!uid || !server}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3.5 rounded-xl hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-flashlight-line"></i>直接購買
                    </span>
                  </button>
                  <a
                    href="https://line.me/ti/p/~@laigame91"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-full bg-green-500 text-white font-bold py-3 rounded-xl hover:bg-green-600 transition-all whitespace-nowrap text-sm flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <i className="ri-customer-service-line"></i>聯繫客服
                  </a>
                </div>

                {/* Trust Badges */}
                <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                  {[
                    { icon: 'ri-shield-check-line', label: '安全保障', color: 'text-green-500' },
                    { icon: 'ri-flashlight-line', label: '3秒入帳', color: 'text-orange-500' },
                    { icon: 'ri-headphone-line', label: '24H客服', color: 'text-indigo-500' },
                  ].map((b, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-2">
                      <div className={`w-6 h-6 flex items-center justify-center mx-auto mb-1 ${b.color}`}>
                        <i className={b.icon}></i>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">{b.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* VIP Hint */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-vip-crown-line text-amber-500 text-lg"></i>
                  <span className="font-bold text-amber-700 text-sm">VIP 會員優惠</span>
                </div>
                <p className="text-xs text-amber-600">VIP會員享有額外 2% OFF 折扣，立即加入享受更多優惠！</p>
                <Link to="/account" className="mt-2 inline-block text-xs font-bold text-amber-600 hover:text-amber-700 underline cursor-pointer">
                  了解VIP制度 →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-gray-800 mb-5 flex items-center gap-2">
            <i className="ri-fire-line text-red-500"></i>
            本月強打推薦
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
            {hotProducts.map(item => (
              <Link
                key={item.id}
                to={item.link}
                className="flex-shrink-0 w-52 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {item.badge && (
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow">
                      {item.badge}
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-gray-800 text-sm mb-1 truncate">{item.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-base font-black text-indigo-600">{item.price}</span>
                    <span className="text-xs text-gray-400 line-through">{item.originalPrice}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
